/** Default GNB brand green */
export const DEFAULT_BRAND_COLOR = "#529B50";

const PRESET_COLORS = [
  "#529B50",
  "#2E7D32",
  "#1565C0",
  "#6A1B9A",
  "#C62828",
  "#EF6C00",
  "#00838F",
  "#37474F",
] as const;

export const BRAND_COLOR_PRESETS = PRESET_COLORS;

function clamp(n: number) {
  return Math.min(255, Math.max(0, Math.round(n)));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace("#", "").trim();
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b]
    .map((v) => clamp(v).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** Mix color toward white (t=0 same, t=1 white) */
function lighten(hex: string, t: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(
    rgb.r + (255 - rgb.r) * t,
    rgb.g + (255 - rgb.g) * t,
    rgb.b + (255 - rgb.b) * t,
  );
}

/** Mix color toward black (t=0 same, t=1 black) */
function darken(hex: string, t: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.r * (1 - t), rgb.g * (1 - t), rgb.b * (1 - t));
}

export function normalizeHex(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

/** Tailwind-like brand scale keyed off a primary (≈ 500) hex */
export function buildBrandPalette(primary: string) {
  const base = normalizeHex(primary) ?? DEFAULT_BRAND_COLOR;
  return {
    25: lighten(base, 0.96),
    50: lighten(base, 0.92),
    100: lighten(base, 0.82),
    200: lighten(base, 0.65),
    300: lighten(base, 0.45),
    400: lighten(base, 0.22),
    500: base,
    600: darken(base, 0.12),
    700: darken(base, 0.28),
    800: darken(base, 0.42),
    900: darken(base, 0.55),
    950: darken(base, 0.72),
  } as const;
}

export function applyBrandPalette(primary: string) {
  if (typeof document === "undefined") return;
  const palette = buildBrandPalette(primary);
  const root = document.documentElement;
  const keys = [
    25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950,
  ] as const;
  keys.forEach((key) => {
    root.style.setProperty(`--color-brand-${key}`, palette[key]);
  });
  root.style.setProperty("--brand-primary", palette[500]);
  const rgb = hexToRgb(palette[500]);
  if (rgb) {
    root.style.setProperty(
      "--shadow-focus-ring",
      `0px 0px 0px 4px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
    );
  }
}
